import { Box, Divider, Link, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useContext } from 'react'
import { Link as RouterLink } from 'react-router-dom'

// others
import dayjs from 'dayjs'
import { BillStatus, ExternalUserCaseRole } from '../../../../../../generated'
import {
  BILL_DELIVERY_CARD_HEADERS,
  NOT_APPLICABLE,
  REMINDER_BILL_ACTIONS,
  VALID_DELIVERY_ACTIONS
} from '../../../../../constants'
import { CaseContext } from '../../../../../context'
import { BillDeliveryCardProps, ClientDeliveryType } from '../../../../../interfaceTypes/props'
import { CardBox } from '../../../../../theme/styleComponents'
import { removeUnderScoreOrHyphenAndToLowerCase, titleize } from '../../../../../utils'
import { DeliveryDetail } from './DeliveryDetail'

export const BillDeliveryCard = ({ caseDetail, bill, refetch }: BillDeliveryCardProps) => {
  const { judgeRoleId } = useContext(CaseContext)
  const { caseToExternalUserToExternalUserRole, bills } = caseDetail || {}
  const billStatusValues = Object.values(BillStatus)

  const trackBills = (bill?.id ? [bill] : bills)
    ?.filter(bill => billStatusValues.includes(bill?.status as BillStatus))
    ?.sort((a, b) => new Date(b?.issueDate).getTime() - new Date(a?.issueDate).getTime())

  // const trackBills = (bill?.id ? [bill] : bills)?.sort((a, b) => new Date(b?.issueDate).getTime() - new Date(a?.issueDate).getTime())

  // Helper to format date as dd/mm/yyyy HH:MM using dayjs, handling numeric timestamps
  const formatDateTime = (dateString: string | number) => {
    if (!dateString) return NOT_APPLICABLE
    let dateNum = dateString
    if (typeof dateString === 'string' && /^\d+$/.test(dateString)) dateNum = parseInt(dateString, 10)

    return dayjs(dateNum).isValid() ? dayjs(dateNum).format('HH:mm MM/DD/YYYY') : NOT_APPLICABLE
  }

  const billDeliveries =
    trackBills?.map(bill => {
      const { billTimelines, id: billNumber, issueDate } = bill || {}

      // Build a map of clientRole to client info for quick lookup
      const clientRoleToInfo: Record<string, { name: string; role: ExternalUserCaseRole }> = {}
      const clients = bill?.case?.caseToExternalUserToExternalUserRole ?? caseToExternalUserToExternalUserRole
      clients
        ?.filter(c => c?.externalUserToExternalUserRole?.externalUserRoleId !== judgeRoleId)
        ?.forEach(caseToExternalUserToExternalUserRole => {
          const { externalUserToExternalUserRole, caseRole } = caseToExternalUserToExternalUserRole
          const { externalUser } = externalUserToExternalUserRole
          const { displayName } = externalUser || {}
          const clientName = displayName || NOT_APPLICABLE
          clientRoleToInfo[removeUnderScoreOrHyphenAndToLowerCase(caseRole || '')] = {
            name: clientName,
            role: caseRole as ExternalUserCaseRole
          }
        })

      // Flatten all delivery records for this bill (not grouped by client)
      const clientDeliveries: ClientDeliveryType[] =
        billTimelines
          ?.filter(billTimeline => VALID_DELIVERY_ACTIONS.includes(billTimeline.action))
          ?.map(billTimeline => {
            const { metadata, deliveryMetadata, action } = billTimeline
            const timelineClientRole = removeUnderScoreOrHyphenAndToLowerCase(metadata?.clientRole || '')
            const clientInfo = clientRoleToInfo[timelineClientRole] || {
              name: NOT_APPLICABLE,
              role: metadata?.clientRole || NOT_APPLICABLE
            }
            return {
              name: clientInfo.name,
              role: clientInfo.role,
              method: deliveryMetadata?.mode ? titleize(deliveryMetadata?.mode) : NOT_APPLICABLE,
              timestamp: formatDateTime(billTimeline?.createdAt),
              status: deliveryMetadata?.status || '',
              content: deliveryMetadata?.content || '',
              isReminder: REMINDER_BILL_ACTIONS.includes(action),
              error: deliveryMetadata?.details
                ? {
                    mode: deliveryMetadata?.mode || '',
                    message: `${deliveryMetadata?.details || ''}`
                  }
                : undefined
            }
          }) || []

      // Determine if any delivery failed
      const success = !clientDeliveries.some(record => !!record.error)

      return {
        billNumber: billNumber || 0,
        type: bill?.type || 'BILL',
        date: new Date(issueDate),
        success,
        amount: bill?.amount || 0,
        amountPaid: bill?.amountPaid || 0,
        writtenOffPrincipal: bill?.writtenOffPrincipal || 0,
        status: bill?.status || '',
        clientDeliveries
      }
    }) || []

  const caseNumber = caseDetail?.caseNumber ?? ''
  const matterId = caseDetail?.id ?? ''

  return (
    <CardBox>
      <Box>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} p={3}>
          <Typography variant="h6" fontSize="18px">
            Bills & Fund Requests
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <Link
              component={RouterLink}
              to={`/billing/bills?search=${encodeURIComponent(caseNumber)}&currentPage=1`}
              sx={{ fontSize: '0.875rem' }}
            >
              View All Bills
            </Link>
            <Link
              component={RouterLink}
              to={`/billing/fund-requests?currentPage=1&matterId=${matterId}`}
              sx={{ fontSize: '0.875rem' }}
            >
              View All Fund Requests
            </Link>
          </Box>
        </Box>

        <Divider />
      </Box>

      {billDeliveries && billDeliveries.length > 0 ? (
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Table sx={{ minWidth: 400 }}>
            <TableHead>
              <TableRow>
                {BILL_DELIVERY_CARD_HEADERS.map((header, index) => (
                  <TableCell
                    key={index}
                    component="th"
                    width={header.width}
                    align={'center'}
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 13, sm: 15 }
                    }}
                  >
                    {header.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {billDeliveries.map((delivery, index) => {
                const rowKey = delivery.billNumber ?? index
                return <DeliveryDetail delivery={delivery} refetch={refetch} key={rowKey} />
              })}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Box>
          <Typography variant="body1" p={2}>
            No Bills Delivered Yet
          </Typography>
        </Box>
      )}
    </CardBox>
  )
}
